import { Fragment, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PERSONNEL_COST_KINDS,
  PERSONNEL_COST_KIND_LABELS,
  PERSONNEL_COST_PERIODS,
  PERSONNEL_COST_PERIOD_LABELS,
  formatCents,
  type CostStructure,
  type FinancingSettings,
  type PersonnelCost,
  type PersonnelCostInput,
} from '@kontor/shared';
import { api } from '../../api';
import { CollapsibleCard } from '../../components/CollapsibleCard';
import { bpText, centsText, parseBp, parseCents } from '../../money';
import { useSession } from '../../session';
import { FinancingTabs } from './FinancingTabs';
import { errorText, formatDay, monthText } from './shared';
import '../fleet/fleet.css';
import './financing.css';

const today = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Berlin' }).format(new Date());

/**
 * Kostenstruktur (KON-256): was ein Monat kostet und was davon übrig bleibt. Einnahmen, veränderliche Kosten und feste Kosten stammen aus den Rechnungen der letzten zwölf Monate, die festen
 * Kosten (Miete, Telefon, Versicherungen ...) aus den Sachkonten der Eingangsrechnungen. Das Personal kommt aus den Vergütungen der Mitarbeiter, dazu Arbeitgeberanteile und weitere Personalkosten.
 * Hier lassen sich die weiteren Personalkosten und die Zuschläge pflegen. Es erscheinen nur Summen, nie die Vergütung eines einzelnen Mitarbeiters.
 */
export function CostsPage() {
  const { can } = useSession();
  const client = useQueryClient();
  const mayWrite = can('financing.write');
  const costs = useQuery({ queryKey: ['financing-costs'], queryFn: async () => (await api<{ data: CostStructure }>('GET', '/financing-costs')).data });
  const settings = useQuery({ queryKey: ['financing-settings'], queryFn: async () => (await api<{ data: FinancingSettings }>('GET', '/financing-costs/settings')).data });
  const personnel = useQuery({ queryKey: ['financing-personnel'], queryFn: async () => (await api<{ data: PersonnelCost[] }>('GET', '/financing-costs/personnel')).data });
  const refresh = () => {
    for (const key of ['financing-costs', 'financing-personnel', 'financing-settings', 'financing-project', 'financing-project-preview']) client.invalidateQueries({ queryKey: [key] });
  };

  const [editing, setEditing] = useState<PersonnelCost | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async (body: PersonnelCostInput) => (editing && editing !== 'new' ? api('PUT', `/financing-costs/personnel/${editing.id}`, body) : api('POST', '/financing-costs/personnel', body)),
    onSuccess: () => { setEditing(null); setError(null); refresh(); },
    onError: (e) => setError(errorText(e, 'Die Personalkosten ließen sich nicht speichern.')),
  });
  const remove = useMutation({
    mutationFn: async (id: string) => api('DELETE', `/financing-costs/personnel/${id}`),
    onSuccess: refresh,
    onError: (e) => setError(errorText(e, 'Die Personalkosten ließen sich nicht löschen.')),
  });

  const c = costs.data;
  const maxRevenue = Math.max(1, ...(c?.months.map((m) => Math.max(m.revenueCents, m.expenseCents)) ?? [1]));

  return (
    <>
      <FinancingTabs />
      <div className="page-head"><h1>Kostenstruktur</h1><span className="muted">{c ? `${formatDay(c.from)} bis ${formatDay(c.to)}` : ''}</span></div>
      <p className="muted">Wirkia legt Ihre Einnahmen, Kosten, Löhne und Raten nebeneinander. Daraus entsteht die Grundlage für die Tragfähigkeit eines <Link to="/finanzierungen/vorhaben">Finanzierungsvorhabens</Link> und für die Mappe an die Bank. Alle Beträge netto, als Durchschnitt pro Monat über die letzten zwölf vollen Monate.</p>
      {costs.isError && <div className="alert alert--error">{(costs.error as Error).message}</div>}

      {c && (
        <>
          <div className="fleet-tiles">
            <div className="card fleet-tile"><span className="muted small">Einnahmen</span><strong>{formatCents(c.revenueMonthlyCents)}</strong><span className="muted small">pro Monat</span></div>
            <div className="card fleet-tile"><span className="muted small">Veränderliche Kosten</span><strong>{formatCents(c.variableMonthlyCents)}</strong><span className="muted small">Kraftstoff, Wartung, Waren, übrige</span></div>
            <div className="card fleet-tile"><span className="muted small">Feste Kosten</span><strong>{formatCents(c.fixedMonthlyCents)}</strong><span className="muted small">aus den Sachkonten</span></div>
            <div className="card fleet-tile"><span className="muted small">Personalkosten</span><strong>{formatCents(c.personnel.totalMonthlyCents)}</strong><span className="muted small">{c.personnel.employeeCount} Mitarbeiter</span></div>
            <div className="card fleet-tile"><span className="muted small">Überschuss vor Raten</span><strong className={c.surplusBeforeDebtCents < 0 ? 'fin-neg' : ''}>{formatCents(c.surplusBeforeDebtCents)}</strong><span className="muted small">pro Monat</span></div>
            <div className="card fleet-tile"><span className="muted small">Bestehende Raten</span><strong>{formatCents(c.existingDebtServiceMonthlyCents)}</strong><span className="muted small">{c.activeFinancings} laufende Verträge, brutto</span></div>
          </div>
          {c.notes.length > 0 && <div className="card fin-notes"><ul>{c.notes.map((n) => <li key={n} className="small">{n}</li>)}</ul></div>}

          <CollapsibleCard title="Feste Kosten aus den Eingangsrechnungen" storageKey="financing-fixed" defaultOpen>
            {c.fixed.length === 0 ? (
              <p className="muted">Es gibt keine Eingangsrechnungen auf Konten, die Wirkia als feste Kosten erkennt (Miete, Telefon und Internet, Versicherungen, Steuerberatung, Software). Ordnen Sie Eingangsrechnungen ein Sachkonto zu, dann erscheinen sie hier.</p>
            ) : (
              <div className="table-card">
                <table className="table table--cards">
                  <thead><tr><th>Gruppe und Konto</th><th className="num">pro Monat</th></tr></thead>
                  <tbody>
                    {c.fixed.map((g) => (
                      <Fragment key={g.group}>
                        <tr><td data-label="Gruppe"><strong>{g.label}</strong></td><td data-label="pro Monat" className="num"><strong>{formatCents(g.monthlyCents)}</strong></td></tr>
                        {g.accounts.map((a) => <tr key={`${g.group}-${a.number}`}><td data-label="Konto" className="muted">{a.number ? `${a.number} ` : ''}{a.name}</td><td data-label="pro Monat" className="num muted">{formatCents(a.monthlyCents)}</td></tr>)}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <p className="muted small">Welche Konten als feste Kosten zählen, erkennt Wirkia an der Bezeichnung des Kontos und seiner Kategorie im Kontenplan. Kraftstoff, Wartung, Waren und alles andere zählt als veränderliche Kosten. Finanzierungen und Leasing sind getrennt unter „Bestehende Raten“ erfasst.</p>
          </CollapsibleCard>

          <CollapsibleCard title="Personalkosten" storageKey="financing-personnel" defaultOpen>
            <dl className="fleet-facts">
              <div><dt>Löhne und Gehälter (brutto)</dt><dd>{formatCents(c.personnel.grossMonthlyCents)}</dd></div>
              <div><dt>Arbeitgeberanteile (geschätzt)</dt><dd>{formatCents(c.personnel.onCostMonthlyCents)}</dd></div>
              <div><dt>Weitere Personalkosten</dt><dd>{formatCents(c.personnel.extraMonthlyCents)}</dd></div>
              <div><dt>Zusammen</dt><dd><strong>{formatCents(c.personnel.totalMonthlyCents)}</strong></dd></div>
            </dl>
            <p className="muted small">Die Löhne stammen aus der Vergütung in der Mitarbeiterakte (Stundensatz mal Wochenstunden oder Pauschalbetrag). Hier erscheint nur die Summe. Die Arbeitgeberanteile (Sozialversicherung, Umlagen) schätzt Wirkia mit einem Zuschlag auf das Bruttoentgelt, weil es keine Lohnabrechnung führt.</p>

            {settings.data && <SettingsForm settings={settings.data} mayWrite={mayWrite} onSaved={refresh} />}

            <h3>Weitere Personalkosten</h3>
            <p className="muted small">Alles, was ein Mitarbeiter außer dem Lohn kostet: Berufsgenossenschaft, Weiterbildung, Arbeitskleidung, Zuschüsse. Tragen Sie es einmal ein, Wirkia rechnet es in den Monat um.</p>
            {error && <div className="alert alert--error" role="alert">{error}</div>}
            {mayWrite && editing === null && <p><button type="button" className="btn btn--secondary" onClick={() => { setError(null); setEditing('new'); }}>Personalkosten hinzufügen</button></p>}
            {editing !== null && <PersonnelCostForm cost={editing === 'new' ? undefined : editing} pending={save.isPending} onSubmit={(b) => { setError(null); save.mutate(b); }} onCancel={() => setEditing(null)} />}
            <div className="table-card">
              <table className="table table--cards">
                <thead><tr><th>Bezeichnung</th><th>Art</th><th className="num">Betrag</th><th className="num">pro Monat</th><th>Gültig</th><th /></tr></thead>
                <tbody>
                  {personnel.data?.length === 0 && <tr><td colSpan={6} className="empty">Noch keine weiteren Personalkosten erfasst.</td></tr>}
                  {personnel.data?.map((r) => (
                    <tr key={r.id}>
                      <td data-label="Bezeichnung"><strong>{r.label}</strong></td>
                      <td data-label="Art">{PERSONNEL_COST_KIND_LABELS[r.kind]}</td>
                      <td data-label="Betrag" className="num">{formatCents(r.amountCents)} {PERSONNEL_COST_PERIOD_LABELS[r.period]}</td>
                      <td data-label="pro Monat" className="num">{formatCents(r.monthlyCents)}</td>
                      <td data-label="Gültig">{formatDay(r.validFrom)}{r.validUntil ? ` bis ${formatDay(r.validUntil)}` : ' unbefristet'}</td>
                      <td>{mayWrite && <span className="row-actions"><button type="button" className="btn btn--secondary btn--sm" onClick={() => { setError(null); setEditing(r); }}>Ändern</button> <button type="button" className="btn btn--secondary btn--sm" onClick={() => { if (window.confirm(`„${r.label}“ löschen?`)) remove.mutate(r.id); }}>Löschen</button></span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CollapsibleCard>

          <CollapsibleCard title="Einnahmen und Ausgaben je Monat" storageKey="financing-months" defaultOpen={false}>
            <div className="table-card">
              <table className="table table--cards fin-forecast">
                <thead><tr><th>Monat</th><th className="num">Einnahmen</th><th /><th className="num">Ausgaben aus Eingangsrechnungen</th></tr></thead>
                <tbody>
                  {c.months.map((m) => (
                    <tr key={m.month}>
                      <td data-label="Monat">{monthText(m.month)}</td>
                      <td data-label="Einnahmen" className="num"><strong>{formatCents(m.revenueCents)}</strong></td>
                      <td className="cell-bar" aria-hidden="true"><span className="fin-forecast-bar" style={{ width: `${Math.round((m.revenueCents / maxRevenue) * 100)}%` }} /></td>
                      <td data-label="Ausgaben" className="num">{formatCents(m.expenseCents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="muted small">Ausgaben ohne Personal und ohne Finanzierungen. Die Monatsbeträge stammen aus den Rechnungen nach Rechnungsdatum, wie im Finanzreport.</p>
          </CollapsibleCard>
        </>
      )}
    </>
  );
}

/** Zuschläge auf den Bruttolohn für die Arbeitgeberanteile */
function SettingsForm({ settings, mayWrite, onSaved }: { settings: FinancingSettings; mayWrite: boolean; onSaved: () => void }) {
  const [regular, setRegular] = useState(bpText(settings.employerOnCostBp));
  const [mini, setMini] = useState(bpText(settings.minijobOnCostBp));
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async () => api('PUT', '/financing-costs/settings', { employerOnCostBp: parseBp(regular), minijobOnCostBp: parseBp(mini) }),
    onSuccess: () => { setError(null); onSaved(); },
    onError: (e) => setError(errorText(e, 'Die Zuschläge ließen sich nicht speichern.')),
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (parseBp(regular) === null || parseBp(mini) === null) return setError('Bitte geben Sie die Zuschläge als Zahl an, zum Beispiel 21.');
    save.mutate();
  };
  return (
    <form className="fleet-form" onSubmit={submit} aria-label="Arbeitgeberanteile">
      <h3>Arbeitgeberanteile</h3>
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <div className="form-grid">
        <label className="field"><span>Zuschlag auf das Bruttoentgelt (%)</span><input className="input" inputMode="decimal" value={regular} onChange={(e) => setRegular(e.target.value)} disabled={!mayWrite} /><small className="muted">Richtwert {bpText(settings.defaults.employerOnCostBp)} %: Anteile des Arbeitgebers an Kranken-, Renten-, Pflege- und Arbeitslosenversicherung und Umlagen.</small></label>
        <label className="field"><span>Zuschlag bei Minijobs (%)</span><input className="input" inputMode="decimal" value={mini} onChange={(e) => setMini(e.target.value)} disabled={!mayWrite} /><small className="muted">Richtwert {bpText(settings.defaults.minijobOnCostBp)} %: Pauschalabgaben und Umlagen.</small></label>
      </div>
      {mayWrite && <div className="form-actions fin-form-actions"><button type="submit" className="btn btn--secondary" disabled={save.isPending}>{save.isPending ? 'Wird gespeichert …' : 'Zuschläge speichern'}</button></div>}
    </form>
  );
}

function PersonnelCostForm({ cost, pending, onSubmit, onCancel }: { cost?: PersonnelCost; pending: boolean; onSubmit: (b: PersonnelCostInput) => void; onCancel: () => void }) {
  const [kind, setKind] = useState(cost?.kind ?? 'accident_insurance');
  const [label, setLabel] = useState(cost?.label ?? '');
  const [amount, setAmount] = useState(cost ? centsText(cost.amountCents) : '');
  const [period, setPeriod] = useState(cost?.period ?? 'year');
  const [from, setFrom] = useState(cost?.validFrom ?? today());
  const [until, setUntil] = useState(cost?.validUntil ?? '');
  const [note, setNote] = useState(cost?.note ?? '');
  const [local, setLocal] = useState<string | null>(null);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const cents = parseCents(amount);
    if (cents === null) return setLocal('Bitte geben Sie den Betrag als Zahl an, zum Beispiel 1.200,00.');
    onSubmit({ kind, label: label.trim(), amountCents: cents, period, validFrom: from, validUntil: until || null, note: note.trim() || null });
  };
  return (
    <form className="card fleet-form" onSubmit={submit} aria-label="Personalkosten">
      <h3>{cost ? 'Personalkosten ändern' : 'Personalkosten hinzufügen'}</h3>
      {local && <div className="alert alert--error" role="alert">{local}</div>}
      <div className="form-grid">
        <label className="field"><span>Art</span><select className="input" value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>{PERSONNEL_COST_KINDS.map((k) => <option key={k} value={k}>{PERSONNEL_COST_KIND_LABELS[k]}</option>)}</select></label>
        <label className="field"><span>Bezeichnung</span><input className="input" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={120} required placeholder="zum Beispiel Beitrag Berufsgenossenschaft" /></label>
        <label className="field"><span>Betrag (€)</span><input className="input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} required /></label>
        <label className="field"><span>Zeitraum des Betrags</span><select className="input" value={period} onChange={(e) => setPeriod(e.target.value as typeof period)}>{PERSONNEL_COST_PERIODS.map((p) => <option key={p} value={p}>{PERSONNEL_COST_PERIOD_LABELS[p]}</option>)}</select></label>
        <label className="field"><span>Gültig ab</span><input className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} required /></label>
        <label className="field"><span>Gültig bis (leer = unbefristet)</span><input className="input" type="date" value={until} onChange={(e) => setUntil(e.target.value)} /></label>
        <label className="field"><span>Bemerkung</span><input className="input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} /></label>
      </div>
      <div className="form-actions fin-form-actions">
        <button type="submit" className="btn btn--primary" disabled={pending}>{pending ? 'Wird gespeichert …' : 'Speichern'}</button>
        <button type="button" className="btn btn--secondary" onClick={onCancel}>Abbrechen</button>
      </div>
    </form>
  );
}
